/**
 * Support Ticket Service
 *
 * API service for support ticket management (admin endpoints).
 */

import { apiClient } from './api.service';
import type {
  SupportTicket,
  SupportTicketDetail,
  TicketQueryParams,
  PaginatedTickets,
  TicketStats,
  AssignTicketDto,
  UpdateTicketStatusDto,
  UpdateTicketPriorityDto,
  ResolveTicketDto,
  CreateMessageDto,
  SupportMessage,
  AgentWorkload,
} from '@/types/support-ticket.types';

// ============================================================================
// SERVICE
// ============================================================================

export const supportTicketService = {
  /**
   * List all tickets (admin, paginated with filters)
   */
  async getAll(
    params: TicketQueryParams,
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedTickets> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.category) searchParams.append('category', params.category);
    if (params.priority) searchParams.append('priority', params.priority);
    if (params.customerId) searchParams.append('customerId', params.customerId);
    if (params.storeId) searchParams.append('storeId', params.storeId);
    if (params.assignedToId)
      searchParams.append('assignedToId', params.assignedToId);
    if (params.unassignedOnly) searchParams.append('unassignedOnly', 'true');
    if (params.countryId) searchParams.append('countryId', params.countryId);
    if (params.search) searchParams.append('search', params.search);

    const response = await apiClient.get<PaginatedTickets>(
      `/support-tickets/admin?${searchParams.toString()}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Get ticket detail with messages (admin)
   */
  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<SupportTicketDetail> {
    const response = await apiClient.get<SupportTicketDetail>(
      `/support-tickets/admin/${id}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Get ticket stats
   */
  /**
   * Conteos para las tarjetas. `countryId` es el mismo que filtra la tabla:
   * sin él, la cabecera contaba toda la plataforma y no cuadraba con las
   * filas de abajo.
   */
  async getStats(
    countryId?: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<TicketStats> {
    const query = countryId
      ? `?countryId=${encodeURIComponent(countryId)}`
      : '';
    const response = await apiClient.get<TicketStats>(
      `/support-tickets/admin/stats${query}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Assign ticket to an agent
   */
  async assign(id: string, data: AssignTicketDto): Promise<SupportTicket> {
    const response = await apiClient.patch<SupportTicket>(
      `/support-tickets/admin/${id}/assign`,
      data
    );
    return response.data;
  },

  /**
   * Update ticket status
   */
  async updateStatus(
    id: string,
    data: UpdateTicketStatusDto
  ): Promise<SupportTicket> {
    const response = await apiClient.patch<SupportTicket>(
      `/support-tickets/admin/${id}/status`,
      data
    );
    return response.data;
  },

  /**
   * Update ticket priority
   */
  async updatePriority(
    id: string,
    data: UpdateTicketPriorityDto
  ): Promise<SupportTicket> {
    const response = await apiClient.patch<SupportTicket>(
      `/support-tickets/admin/${id}/priority`,
      data
    );
    return response.data;
  },

  /**
   * Resolve ticket
   */
  async resolve(id: string, data: ResolveTicketDto): Promise<SupportTicket> {
    const response = await apiClient.patch<SupportTicket>(
      `/support-tickets/admin/${id}/resolve`,
      data
    );
    return response.data;
  },

  /**
   * Reabrir ticket cerrado/resuelto (admin).
   */
  async reopen(id: string): Promise<SupportTicket> {
    const response = await apiClient.patch<SupportTicket>(
      `/support-tickets/admin/${id}/reopen`,
      {},
    );
    return response.data;
  },

  /**
   * Send message in ticket (admin, supports internal notes)
   */
  async sendMessage(
    id: string,
    data: CreateMessageDto
  ): Promise<SupportMessage> {
    const response = await apiClient.post<SupportMessage>(
      `/support-tickets/admin/${id}/messages`,
      data
    );
    return response.data;
  },

  /**
   * Get agent workload (active tickets per agent)
   */
  async getAgentWorkload(
    countryId?: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<AgentWorkload[]> {
    const params = countryId ? `?countryId=${countryId}` : '';
    const response = await apiClient.get<AgentWorkload[]>(
      `/support-tickets/admin/agents-workload${params}`,
      { signal: opts.signal },
    );
    return response.data;
  },
};
